import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'meus-arquivos:favoritos:v1';

/** Uma lista por usuário (mesmo navegador pode ter mais de um login). */
export function favoritesStorageKey(userId: number): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

/** Qualquer falha (bloqueio, JSON inválido, formato errado) vale como "nada salvo". */
function readFavorites(storageKey: string): string[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(storageKey) ?? '[]');
    return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : [];
  } catch {
    return [];
  }
}

function writeFavorites(storageKey: string, favorites: string[]) {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(favorites));
  } catch {
    // Storage bloqueado/cheio: o favorito vale até recarregar a página.
  }
}

/**
 * Pastas fixadas no "Acesso rápido". Guardadas pelo caminho de rótulos (`-PREVS/diario`), porque o
 * id de pasta do backend muda a cada request. Não há endpoint de preferências para isso, então
 * ficam no localStorage — por usuário e por navegador.
 */
export default function useFileViewerFavorites(userId: number) {
  const storageKey = favoritesStorageKey(userId);
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites(storageKey));

  useEffect(() => {
    setFavorites(readFavorites(storageKey));

    // Outra aba fixou/desafixou: acompanha sem recarregar.
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) setFavorites(readFavorites(storageKey));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const isFavorite = useCallback((key: string) => favorites.includes(key), [favorites]);

  /** Alterna e devolve se a pasta ficou fixada. */
  const toggleFavorite = useCallback((key: string): boolean => {
    const pinned = !favorites.includes(key);
    const next = pinned ? [...favorites, key] : favorites.filter((item) => item !== key);
    setFavorites(next);
    writeFavorites(storageKey, next);
    return pinned;
  }, [favorites, storageKey]);

  return { favorites, isFavorite, toggleFavorite };
}
